
import React, { useState, useCallback, forwardRef, useRef, useImperativeHandle, useMemo } from 'react';
import { Typography, Row, Col, Button, Alert, Form, Divider } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import _ from 'lodash';
import BaseModal from 'components/BaseModal';
import { BASE_GUTTER, MODAL_SIZE_LARGE } from "constants/Layout";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import { cvssVectorValidator, extractCVSSVersion, getCvssScoreFromVector, parseCVSSVectorStringToFields, validateCVSSVectorFormat } from 'utils/cvssUtil';
import { CVSS_FIELDS } from 'constants/CVSSConstants';
import useModalRenderer from 'hooks/useModalRenderer';
import NewCVSSCalculator from 'components/cvss/NewCVSSCalculator';
const { Text } = Typography;

export const getCVSSField = (form, isDisabled) => {
    return [
        {
            name: 'cvssVector',
            colSpan: 12,
            label: "CVSS Vector (CVSS 2.0, 3.0, 3.1, 4.0)",
            disabled: isDisabled,
            rules:[
                {
                    ...cvssVectorValidator()
                }
            ],
            props: {
                onChange: (e) => handleVectorChange(form, e),
            }
        },
        {
            name: 'cvssScore',
            colSpan: 12,
            label: "Base Score",
            type: FieldTypes.NUMBER,
            disabled: isDisabled,
        },
    ]
}

const handleVectorChange = (form, e) => {
    const vector = e.target.value; 
    const score = getCvssScoreFromVector(vector); 
    form.setFieldsValue({ cvssScore: score });      // This is to set the Base Score in the manual finding cvss field as soon as valid Vector is entered.
}


const ManualFindingsCVSSFields = ({form, subProductId, environmentName}) => {
    const [isDisabled, setIsDisabled] = useState(false);
    const fields =  getCVSSField(form,isDisabled );
    const modalRenderer = useModalRenderer();

    const showNewCVSSCalculator = useCallback(() => {
        const data = form.getFieldsValue();
        modalRenderer.show({
            content: (
                <NewCVSSCalculator
                    initialCvssVector={validateCVSSVectorFormat(data.cvssVector) ? data.cvssVector : ""}
                    selectedCVE={form.getFieldValue("cve")}
                    finding={{ subProduct: { id: subProductId }, envName: environmentName }}
                />
            ),
            onClose: (data) => {
                if (!_.isEmpty(data)) {
                    form.setFieldsValue(data);
                    setIsDisabled(true);
                }
            },
            width: 1056,
        })
    }, [form, modalRenderer, subProductId, environmentName]);

    return (
        <>
        <Alert className='m-b-m p-l-0 p-r-0' type="info" description={
            <>
                <Row gutter={BASE_GUTTER}>
                    <Col span={24}>
                        <Text>Set Common Vulnerability Scoring System values from the </Text>
                        <Button className='m-0 p-0' type="link" onClick={() => showNewCVSSCalculator()}>CVSS Calculator (2.0/3.0/3.1/4.0)</Button>
                    </Col>
                </Row>
                {generateFormFields(fields)}
                {isDisabled && <div><b>Note:</b> "CVSS Vector" and "Base Score" are added via calculator, click "CVSS Calculator" to update.</div>}
            </>
        } />
        {modalRenderer.context}
        </>
    )
}

export default ManualFindingsCVSSFields;


export const CVSSCalculator = forwardRef(({ afterClose, initialCvssVector = '', disabled }, ref) => {

    const [form] = useForm();

    const calculatorRef = useRef();
    const isSubmittedSuccessfully = useRef(false); // Add this ref to track submit status

    const onSubmit = useCallback(async () => {
        await form.validateFields();
        isSubmittedSuccessfully.current = true; // Set to true on successful submission
    }, [form])
   
    const baseModalProps = {
		title: 'CVSS Calculator',
		okText: 'Apply',
        width: MODAL_SIZE_LARGE,
		onOk: onSubmit,
        okButtonProps: {disabled}, 
	};

    const handleAfterClose = useCallback(() => {
        afterClose(isSubmittedSuccessfully.current ?  calculatorRef.current :  null);
        isSubmittedSuccessfully.current = false; // Reset after closing
    }, [calculatorRef, afterClose])

    return (
        <BaseModal
            ref={ref}
            modalProps={baseModalProps}
            afterClose={handleAfterClose}
        >               
             <CVSSVectorGenerator
                disabled={disabled}
                value={initialCvssVector}
                ref={calculatorRef}
                form = {form}
            />
        </BaseModal>
    )
})

export const CVSSVectorGenerator = forwardRef(({ disabled, value, onChange, form: _form }, ref) => {
    const [form] = useForm(_form);

    const [cvssScore, setCVSSScore] = useState(
        () => getCvssScoreFromVector(value)
    );
    const [cvssVector, setCVSSVector] = useState(value);
  
    const [version, setActiveVersion] = useState(() => extractCVSSVersion(value) || "none"); // Track the active tab
    
    const [initialValues] = useState(() => ({
        ...parseCVSSVectorStringToFields(value),
        version,
        cvssVectorManual: value
    }));

    useImperativeHandle(ref, () => ({
        cvssScore,
        cvssVector,
    }))

    const generateFields = useCallback(() => {
        if (version==="none") return [];
        return CVSS_FIELDS[version].map(field => ({
            ...field,
            type: FieldTypes.RADIO,
            required: true, 
            props: {
                radioClassname: 'white-space-nowrap',
                buttonStyle: 'solid',
                itemTypeButton: true,
                size: 'small',
                disabled: disabled, 
            }
        }));
    }, [version, disabled]);

    const fields = useMemo(() => generateFields(), [generateFields]);

    const generateCVSSString = useCallback((val) => {
        if(val.version){
            setActiveVersion(val.version);
            const fieldValues = {};
            [...fields.map(f => f.name), "cvssVectorManual"].forEach(key => fieldValues[key] = undefined);
            form.setFieldsValue(fieldValues);
        }
        const data = form.getFieldsValue();
        const version = data.version;
        const fieldNames = fields.map(f => f.name);
        const values = _.omitBy(_.pick(data, fieldNames), _.isNil);
        
            const CVSS_PREFIX =`CVSS:${version}`;

            if(version!=="none"){
                let str = CVSS_PREFIX;
                _.keys(values).forEach(k => {
                    const key = k.slice(0, -2);
                    str += `/${key}:${values[k]}`;
                });
        
                if(validateCVSSVectorFormat(str)){
                    setCVSSScore(getCvssScoreFromVector(str));
                    form.setFieldsValue({ cvssVectorManual: str, cvssVector: str });
                }
                setCVSSVector(prev => {
                    if(prev !== str){
                        // setimeout to avoid recursive/circular rerender
                        setTimeout(() => {
                            // trigger onChange only if the actual value changes to avoid circular onChange and setFieldsValue
                            onChange && onChange(str);
                        }, 100);
                        return str;
                    }
                    return prev;
                });
            }
            else {
                setCVSSScore(null);
                setCVSSVector((prev) => {
                    // setimeout to avoid recursive/circular rerender
                    if (prev !== null) {
                        setTimeout(() => {
                            // trigger onChange only if the actual value changes to avoid circular onChange and setFieldsValue
                            onChange && onChange(null);
                        }, 100);
                    }
                    return null;
                });
                form.resetFields([...fields.map(f => f.name), "cvssVectorManual"]);
            }
    }, [form, fields, onChange]);

    const vectorFieldManual = [
        {
            name: 'cvssVectorManual',
            label: 'CVSS Vector',
            colSpan: 24,
            values: cvssVector,
            disabled: disabled,
            props: {
                onChange:(e) => {
                    const vector = e.target.value;
                    if (validateCVSSVectorFormat(vector)) {
                        const newParsedValues = parseCVSSVectorStringToFields(vector);
                        form.setFieldsValue(newParsedValues);
                        setCVSSVector(vector);
                        setCVSSScore(getCvssScoreFromVector(vector));
                        form.setFieldsValue({ cvssVector: vector });
                    }
                    else {
                        // return Promise.reject('Not a valid CVSS vector.');
                    }   
                }
            },
            rules:[
                {
                    validator: (_rule, value)=>{
                        if (validateCVSSVectorFormat(value)) {
                            const _version = value ? extractCVSSVersion(value): null ;
                            if(version !== _version){
                                return Promise.reject(`Vector is not CVSS ${version}`);
                            }
                        }
                        return Promise.resolve();
                    }
                },
                {
                    ...cvssVectorValidator()
                }
            ]
        },
    ];


    return (
        <>
            <Form form={form} className="cvss-calculator-form"
                layout="vertical"
                onValuesChange={generateCVSSString}
                initialValues={initialValues}
            >       
                {generateFormFields([{
                    name: "version",
                    type: FieldTypes.RADIO,
                    disabled: disabled,
                    options: ["2.0", "3.0", "3.1", "4.0"].map(v => ({label: `CVSS ${v}`, value: v})).concat([{label: "None", value: "none"}]),
                    props: {
                        buttonStyle: 'solid',
                        itemTypeButton: true,
                        size: 'small',
                        radioClassname: 'white-space-nowrap',
                    }
                }])}      
                <Alert className='m-b-m' type="info" description={
                    <>
                        {version==="4.0" ? (
                            Object.entries(_.groupBy(fields, 'group'))
                            .map(([groupName, groupedFields], index) => (
                                <React.Fragment key={index}>
                                    <Typography.Title level={5}>{groupName}</Typography.Title>
                                    {generateFormFields(groupedFields)}
                                    {index < 2 && <Divider style={{ borderTopWidth: '2px', margin: '0px auto 18px auto' }} />}
                                </React.Fragment>
                            ))
                        ) : (
                            generateFormFields(fields)
                        )}
                    {version === "none" ?  
                        <Typography.Text> Click Submit/Apply to reset the value of CVSS Vector and Base Score </Typography.Text>
                    : null }
                    </>
                }/>
                {version !== "none" ? generateFormFields(vectorFieldManual) : null}
            </Form>
            {
                <Row gutter={BASE_GUTTER}>                  
                    {/* <Col span={24}>
                        <Form 
                            form={form} 
                            className="cvss-calculator-form" 
                            onValuesChange={generateCVSSString}
                            layout="vertical"
                        >
                            {version !== "none" ? generateFormFields(vectorFieldManual) : null}
                        </Form>
                                
                    </Col> */}
                    {
                        version==="none"? null : <Col span={10}>
                            <Typography.Text strong>Base Score: </Typography.Text><Typography.Text> {cvssScore} </Typography.Text>
                        </Col>
                    }
                </Row>
          
            }
        </>
    );
});
